import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * HeraldPage — the admin-desk product landing. Public-safe.
 * Reads from /api/sage/herald/desk/handshake and /api/sage/herald/desk/meter.
 * Master-gated write actions land elsewhere (not on this public page).
 */
export default function HeraldPage() {
  const [handshake, setHandshake] = useState(null);
  const [meter, setMeter] = useState(null);
  const [board, setBoard] = useState(null);
  const [journal, setJournal] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [h, m, b, j] = await Promise.all([
          fetch(`${BACKEND_URL}/api/sage/herald/desk/handshake`).then((r) => r.ok ? r.json() : null),
          fetch(`${BACKEND_URL}/api/sage/herald/desk/meter`).then((r) => r.ok ? r.json() : null),
          fetch(`${BACKEND_URL}/api/sage/herald/board`).then((r) => r.ok ? r.json() : null),
          fetch(`${BACKEND_URL}/api/sage/herald/journal`).then((r) => r.ok ? r.json() : null),
        ]);
        setHandshake(h); setMeter(m); setBoard(b); setJournal(j);
      } catch {}
    })();
  }, []);

  return (
    <div className="min-h-screen bg-herald-bg text-herald-text sage-grain relative">
      <TopNav />

      <section className="border-b border-herald-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-herald-accent mb-4">
            <ProductMark slug="herald" size={48} />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-herald-accent2 mb-2">Herald</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase text-herald-text mb-6">
            The Founder's admin desk.
          </h1>
          <p className="text-base leading-relaxed sage-prose max-w-3xl mb-4">
            Herald is the desk assistant of the Sage house: inbox digest, task board, one-line-a-day journal. Master-gated. Warden-graded on every entry. Zero mock rows.
          </p>
          {handshake && (
            <p className="text-[10px] uppercase tracking-[0.15em] text-herald-accent2" data-testid="herald-status">
              Status: {handshake.status} · Role: {handshake.role}
            </p>
          )}
        </div>
      </section>

      <section className="border-b border-herald-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-herald-accent2 mb-6">§ The desk in numbers</div>
          {meter ? (
            <div className="grid md:grid-cols-4 gap-4" data-testid="herald-meter">
              <div className="border border-herald-border p-6">
                <div className="text-[10px] uppercase tracking-[0.15em] text-herald-accent2 mb-1">Digests filed</div>
                <div className="text-4xl font-bold text-herald-text">{meter.digests_filed}</div>
              </div>
              <div className="border border-herald-border p-6">
                <div className="text-[10px] uppercase tracking-[0.15em] text-herald-accent2 mb-1">Open tasks</div>
                <div className="text-4xl font-bold text-herald-text">{meter.tasks_open}</div>
              </div>
              <div className="border border-herald-border p-6">
                <div className="text-[10px] uppercase tracking-[0.15em] text-herald-accent2 mb-1">Tasks done</div>
                <div className="text-4xl font-bold text-herald-text">{meter.tasks_done}</div>
              </div>
              <div className="border border-herald-border p-6">
                <div className="text-[10px] uppercase tracking-[0.15em] text-herald-accent2 mb-1">Journal lines</div>
                <div className="text-4xl font-bold text-herald-text">{meter.journal_lines}</div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-herald-accent2">Meter loading…</p>
          )}
        </div>
      </section>

      <section className="border-b border-herald-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-herald-accent2 mb-6">§ Board (public lane counts only)</div>
          {board ? (
            <div className="grid grid-cols-4 gap-4" data-testid="herald-board">
              {Object.entries(board.lanes || {}).map(([lane, count]) => (
                <div key={lane} className="border border-herald-border p-4">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-herald-accent2 mb-1">{lane}</div>
                  <div className="text-3xl font-bold text-herald-text">{count}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-herald-accent2">Board loading…</p>
          )}
        </div>
      </section>

      <section className="border-b border-herald-border">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-herald-accent2 mb-6">§ Recent journal (public)</div>
          {journal && journal.lines && journal.lines.length > 0 ? (
            <ul className="text-sm space-y-2" data-testid="herald-journal">
              {journal.lines.map((row) => (
                <li key={row.id} className="border-l-2 border-herald-accent pl-3">
                  <span className="text-herald-accent2 text-[10px] uppercase mr-2">{row.at?.substring(0, 10)}</span>
                  <span className="text-herald-text">{row.line}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-herald-accent2">No journal lines filed yet. Zeros are honestly zeros.</p>
          )}
        </div>
      </section>
    </div>
  );
}
